/**
 * useArloRecommendations.js - Fetches and resolves the Arlo recommendation rails
 *
 * Modeled on useContinueWatching.js. No-ops for unauthenticated users. Arlo
 * composes up to 8 named rails per response (its LLM home layout plus
 * deterministic rails like Top-10).
 *
 * Two-phase render so the rail paints without waiting on the network:
 *   Phase 1 (synchronous) — items already in the useAppLoad payload render as
 *     their real channel; items that aren't render as a synthetic tile painted
 *     from the inline title + title_card art the rails response now carries
 *     (@arlotv/embed-core >= 0.6, RailItem.images.title_card). No hydration on
 *     the paint path.
 *   Phase 2 (background) — the off-homepage ids are hydrated one channel at a
 *     time, synthetic tiles are upgraded to their real channel, and anything
 *     that still can't be resolved is dropped (covers catalog ingest lag /
 *     retired titles, per Arlo's docs).
 *
 * So every *settled* tile is a real channel with correct routing/entitlement;
 * a synthetic tile is only ever the sub-second placeholder before its upgrade
 * (hover-prefetch warms the channel cache well before any click). A rail with
 * no renderable items is dropped whole. Any failure collapses to
 * { arloRails: [] } — never throws, never renders a partial/error state.
 */
import { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useAppLoad } from '../contexts/AppLoadContext';
import profileService from '../services/ProfileService';
import arloService from '../services/ArloService';
import { buildChannelLookups } from '../utils/channelLookups';
import { hydrateMissingChannels } from '../utils/arloHydration';
import { parentSlugOf } from '../utils/arloSeasons';
import { resolveCustomerId } from '../utils/arloIdentity';
import {
  readStoredReactions,
  fetchWatchlistFavorites,
  fetchWatchedSignals,
} from '../utils/arloSignals';

// Arlo's rails index is season-granular, so a series can arrive as season slugs
// (`dragnet-season-2`) — sometimes several seasons of one show, or a season
// alongside its own parent. Sports.TV only ever wants the parent tile, so every
// item resolves to its parent slug when it's a season. Interim client guard; the
// durable fix is server-side in Arlo (see arloSeasons.js).
const resolveTarget = contentId => parentSlugOf(contentId) || contentId;

// A rail item painted straight from the inline data Arlo returns, for a title
// not in the homepage payload. Carries only what ChannelCard needs to draw
// (title + spotlight_poster art) and what a click needs to route (slug). It is
// replaced by the real channel once hydration completes; `__arloPending` marks
// that it is not yet a full channel object. Skipped when there is no title_card
// to paint — that item simply waits for hydration rather than drawing blank.
const syntheticTile = (item, targetId) => {
  const titleCard = item.images?.title_card;
  if (!titleCard) {return null;}
  const title = item.title || targetId;
  return {
    _id: `arlo-pending-${targetId}`,
    slug: targetId,
    name: title,
    title,
    spotlight_poster: titleCard,
    poster: item.images?.poster || null,
    arloRecId: item.rec_id,
    __arloPending: true,
  };
};

// Assemble the rail list from Arlo's response using `itemToChannel` to turn each
// item into a renderable channel (or null to skip it). Collapses seasons to the
// parent, dedupes within a rail (first occurrence wins), and drops any rail left
// with no items. Shared by both phases so paint and settle agree on structure.
const buildRails = (rails, attribution, itemToChannel) =>
  (rails || [])
    .map(rail => {
      const seen = new Set();
      const channels = (rail.items || [])
        .map(item => {
          const targetId = resolveTarget(item.content_id);
          const channel = itemToChannel(item, targetId);
          if (!channel) {return null;}
          const key = channel._id || channel.slug;
          if (seen.has(key)) {return null;}
          seen.add(key);
          return channel;
        })
        .filter(Boolean);
      if (channels.length === 0) {return null;}

      const title = rail.title || 'For You';
      return {
        _id: `arlo-${rail.rail_id}`,
        railId: rail.rail_id,
        name: title,
        display_name: title,
        channels,
        attribution: attribution || null,
      };
    })
    .filter(Boolean);

export const useArloRecommendations = () => {
  const [arloRails, setArloRails] = useState([]);
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth0();
  const { categories } = useAppLoad();

  useEffect(() => {
    let cancelled = false;

    const fetchArloRails = async () => {
      if (!isAuthenticated || !categories || categories.length === 0) {
        setArloRails([]);
        return;
      }

      const customerId = resolveCustomerId();
      if (!customerId) {
        setArloRails([]);
        return;
      }

      setLoading(true);

      try {
        const { bySlug, byId } = buildChannelLookups(categories);
        const currentProfile = profileService.getCurrentProfile();
        const { likes, dislikes } = readStoredReactions(currentProfile?._id, byId);

        const [favorites, watched] = await Promise.all([
          fetchWatchlistFavorites(),
          fetchWatchedSignals(byId),
        ]);

        const { rails, attribution } = await arloService.getRails({
          customerId,
          favorites,
          likes,
          dislikes,
          watched,
        });

        if (cancelled) {return;}

        // Arlo's docs describe content_id as a catalog slug, but live testing against
        // this org's catalog has shown channel _ids too — check both, _id first.
        const localChannel = contentId => byId.get(contentId) || bySlug.get(contentId);

        // Shallow-copy resolved channels: the lookup maps hand back the same objects
        // every other rail renders, and arloRecId must not leak onto those.
        const realTile = (item, channel) => ({ ...channel, arloRecId: item.rec_id });

        // Phase 1 — paint now. Homepage-payload items render as their real
        // channel; the rest render from inline title_card art (or are skipped
        // until hydration when there's no art to paint). No network on this path.
        const paintedRails = buildRails(rails, attribution, (item, targetId) => {
          const local = localChannel(targetId);
          return local ? realTile(item, local) : syntheticTile(item, targetId);
        });
        setArloRails(paintedRails);

        // Phase 2 — settle. Hydrate everything not in the payload, then rebuild
        // with real channels only, dropping anything that still won't resolve
        // (ingest lag / retired titles). Synthetic tiles become real or vanish.
        const missingIds = (rails || [])
          .flatMap(rail => rail.items || [])
          .map(item => resolveTarget(item.content_id))
          .filter(id => id && !localChannel(id));
        const hydrated = await hydrateMissingChannels(missingIds);

        if (cancelled) {return;}

        const settledRails = buildRails(rails, attribution, (item, targetId) => {
          const channel = localChannel(targetId) || hydrated.get(targetId);
          return channel ? realTile(item, channel) : null;
        });

        console.info(
          '🎬 Arlo rails settled:',
          (rails || [])
            .map(rail => {
              const settled = settledRails.find(r => r.railId === rail.rail_id);
              return `${rail.rail_id} ${settled ? settled.channels.length : 0}/${(rail.items || []).length}`;
            })
            .join(', '),
        );

        setArloRails(settledRails);
      } catch (error) {
        console.warn('useArloRecommendations: Failed to load Arlo rails:', error);
        if (!cancelled) {setArloRails([]);}
      } finally {
        if (!cancelled) {setLoading(false);}
      }
    };

    fetchArloRails();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, categories]);

  return { arloRails, loading };
};

export default useArloRecommendations;
